import React, { useCallback } from "react";

import PropTypes from "prop-types";
import { useOrcaCommonContext } from "../contexts/OrcaCommonContext";
import { useTransitionToVideo } from "../hooks/useTransitionToVideo";
import AdWrapper from "../ads/AdWrapper";
import { AdControlsProvider } from "../ads/contexts/AdControlsContext";
import AdContainer from "../ads/AdContainer";
import { VideoEvent } from "../../lib/analytics/utils";
import { PerformanceEvents } from "../../globals";
import { useVideoDataContext } from "../contexts/VideoDataContext";
import { useVideoAnalyticsContext } from "../contexts/VideoAnalyticsContext";
import { usePerformanceContext } from "../contexts/PerformanceContext";
import { useAdsElementsRefContext } from "../contexts/AdsElementsRefContext";
import { usePromoContext } from "../contexts/PromoContext";
import { useOrcaAdsContext } from "../contexts/OrcaAdsContext";
import { Promo } from "../components/Promo";

export const AdsUI = ({
  promoEnabled = true,
  config = {},
  onPromoClick,
  updateDisplay = () => {},
  isCarousel = false,
  children
}) => {
  const {
    aspectRatio: _aspectRatio,
    aspectWidth = 16,
    aspectHeight = 9,
    playCallback = () => {},
    debug
  } = config;

  const { videoData } = useVideoDataContext();
  const { sendAnalyticsEvent } = useVideoAnalyticsContext();
  const { sendPerformanceEvent } = usePerformanceContext();
  const { adControlsRef } = useAdsElementsRefContext();
  const { setRenderControls, promoElement, play, autoplay } =
    useOrcaCommonContext();
  const { promoState, setPromoState } = usePromoContext();
  const { isAdsInitialized } = useOrcaAdsContext();

  const aspectRatio = _aspectRatio || aspectHeight / aspectWidth;

  const transitionToVideo = useTransitionToVideo({
    setRenderControls,
    isCarousel,
    autoplay,
    play,
    playCallback
  });

  const handlePromoClick = useCallback(() => {
    sendAnalyticsEvent(VideoEvent.PROMO_CLICK);
    sendPerformanceEvent(PerformanceEvents.PROMO_CLICK);
    onPromoClick();
  }, [onPromoClick, sendAnalyticsEvent, sendPerformanceEvent]);

  const streamAspectRatio =
    videoData?.streams?.[0].height / videoData?.streams?.[0].width;

  const aspectMatch = aspectRatio === streamAspectRatio;

  const adContainer = (
    <AdContainer
      {...{
        setPromoState,
        transitionToVideo,
        debug,
        setRenderControls,
        updateDisplay,
        isAdsInitialized
      }}
      ref={adControlsRef}
    />
  );

  return (
    <>
      <Promo
        {...{
          promoState,
          setPromoState,
          config,
          promoEnabled,
          aspectRatio,
          aspectMatch
        }}
        onClick={handlePromoClick}
        ref={promoElement}
      />
      <AdWrapper config={config}>
        <AdControlsProvider
          isPreroll={false}
          transitionToVideo={transitionToVideo}
        >
          {children(adContainer)}
        </AdControlsProvider>
      </AdWrapper>
    </>
  );
};

AdsUI.propTypes = {
  config: PropTypes.object,
  children: PropTypes.func.isRequired,
  promoEnabled: PropTypes.bool,
  onPromoClick: PropTypes.func,
  updateDisplay: PropTypes.func,
  isCarousel: PropTypes.bool
};
